import { FC, useEffect, useMemo, useState } from 'react'

import { OperatorView, OperatorViews } from '@img-arena/consoles-api-streaming'
import { PieChart } from '@/components/charts/PieChart'
import { ChartDataItem } from '@/components/charts/types'
import { HorizontalTable } from '@/components/horizontalTable/HorizontalTable'
import { HorizontalTableSkeleton } from '@/components/horizontalTable/HorizontalTableSkeleton'
import { streamingRestApi } from '@/service/streamingApi'
import { generateRainbowPalette, getContrastingColor } from '@/utils/colors'

import { StatisticsCard, StatisticsCardSkeleton } from '../../StatisticsCard'
import { STATISTICS_CSV_HEADERS } from '../constants'
import { ComponentWithStatisticsQueryParams, StatisticsCardTableData } from '../types'
import { OPERATOR_VIEWS_ROWS } from './topOperators.table'

export const TOP_OPERATORS_CARD_TEST_ID = 'top-operators-card' as const
export const TOP_OPERATORS_SKELETON_TEST_ID = 'top-operators-skeleton' as const

const CSV_HEADERS = [
  STATISTICS_CSV_HEADERS.OPERATOR_NAME,
  STATISTICS_CSV_HEADERS.TOTAL_VIEWS,
  STATISTICS_CSV_HEADERS.TOTAL_BYTES,
  STATISTICS_CSV_HEADERS.TOTAL_DURATON,
  STATISTICS_CSV_HEADERS.AVERAGE_DURATION,
  STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS,
  STATISTICS_CSV_HEADERS.UNIQUE_VIEWS
]

export type OperatorViewsTableData = StatisticsCardTableData<OperatorView>

export const TopOperators: FC<ComponentWithStatisticsQueryParams> = ({ queryParams }) => {
  const analyticsEndpoints = streamingRestApi.endpoints.analytics

  const [operatorViewsData, setOperatorViewsData] = useState<OperatorViews['views'] | null>(null)
  const [highlightedItem, setHighlightedItem] = useState<ChartDataItem | null>(null)

  useEffect(() => {
    const ac = new AbortController()

    analyticsEndpoints.getOperatorViews(queryParams, ac.signal).then((data) => {
      setOperatorViewsData(data.views)
    })

    return () => {
      ac.abort()
    }
  }, [...Object.values(queryParams)])

  const operatorColors = useMemo(() => {
    if (!operatorViewsData?.length) return

    return generateRainbowPalette(operatorViewsData?.length, {
      saturation: 50
    })
  }, [operatorViewsData?.length, generateRainbowPalette])

  const tableData: OperatorViewsTableData[] = useMemo(() => {
    if (!operatorViewsData || !operatorColors) return []

    return operatorViewsData.map((item, index) => {
      const color = operatorColors[index]

      const {
        operatorName,
        operatorId,
        averageDuration,
        percentageViews,
        totalBytes,
        totalDuration,
        totalViews,
        uniqueViews
      } = item

      return {
        operatorName,
        operatorId,
        averageDuration,
        percentageViews,
        totalBytes,
        totalViews,
        totalDuration,
        uniqueViews,
        label: operatorName,
        color,
        textColor: getContrastingColor(color)
      }
    })
  }, [operatorViewsData, operatorColors])

  const chartData = useMemo(() => {
    if (!tableData) return []

    return tableData.reduce((acc, item) => {
      acc.push({
        id: item.operatorName,
        value: item.totalViews,
        label: item.label,
        color: item.color,
        textColor: item.textColor,
        visible: true
      })

      return acc
    }, [] as ChartDataItem[])
  }, [tableData])

  const csvData = useMemo(() => {
    if (!operatorViewsData?.length) return

    const data = operatorViewsData.map(
      ({
        operatorName,
        totalViews,
        totalBytes,
        totalDuration,
        averageDuration,
        percentageViews,
        uniqueViews
      }) => ({
        [STATISTICS_CSV_HEADERS.OPERATOR_NAME]: operatorName,
        [STATISTICS_CSV_HEADERS.TOTAL_VIEWS]: totalViews,
        [STATISTICS_CSV_HEADERS.TOTAL_BYTES]: totalBytes,
        [STATISTICS_CSV_HEADERS.TOTAL_DURATON]: totalDuration,
        [STATISTICS_CSV_HEADERS.AVERAGE_DURATION]: averageDuration,
        [STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS]: percentageViews,
        [STATISTICS_CSV_HEADERS.UNIQUE_VIEWS]: uniqueViews
      })
    )

    return {
      csvHeaders: CSV_HEADERS,
      data
    }
  }, [operatorViewsData])

  if (!operatorViewsData) {
    return (
      <StatisticsCardSkeleton testId={TOP_OPERATORS_SKELETON_TEST_ID} height={500}>
        <HorizontalTableSkeleton columns={7} rows={4} />
      </StatisticsCardSkeleton>
    )
  }

  return (
    <StatisticsCard title="Top Operators" testId={TOP_OPERATORS_CARD_TEST_ID} csvData={csvData}>
      <PieChart
        pieSeriesProps={{
          innerRadius: 50
        }}
        onHighlightChange={setHighlightedItem}
        chartData={chartData}
      />
      <HorizontalTable
        data={tableData}
        rows={OPERATOR_VIEWS_ROWS}
        shouldHighlightColumn={(column) => column?.operatorName === highlightedItem?.id}
      />
    </StatisticsCard>
  )
}
